import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Layers, 
  Plus, 
  Building2,
  Users,
  MapPin,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  UserCog,
  DollarSign,
  Target,
  Search
} from "lucide-react";
import { toast } from "sonner";

export default function ClusterManagementPage() {
  const { user, isSuperAdmin } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    head_user_id: "",
    clinic_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clustersRes, clinicsRes, usersRes] = await Promise.all([
        apiClient.get("/clusters"),
        apiClient.get("/clinics"),
        apiClient.get("/users")
      ]);
      setClusters(clustersRes.data);
      setClinics(clinicsRes.data);
      setUsers(usersRes.data.filter(u => u.role === "head" || u.role === "super_admin"));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditMode(false);
    setFormData({
      id: null,
      name: "",
      description: "",
      head_user_id: "",
      clinic_ids: []
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (cluster) => {
    setEditMode(true);
    setFormData({
      id: cluster.id,
      name: cluster.name,
      description: cluster.description || "",
      head_user_id: cluster.head_user_id || "",
      clinic_ids: cluster.clinic_ids || []
    });
    setDialogOpen(true);
  };

  const handleClinicToggle = (clinicId) => {
    setFormData(prev => ({
      ...prev,
      clinic_ids: prev.clinic_ids.includes(clinicId)
        ? prev.clinic_ids.filter(id => id !== clinicId)
        : [...prev.clinic_ids, clinicId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Cluster name is required");
      return;
    }
    
    if (formData.clinic_ids.length === 0) {
      toast.error("Select at least one clinic for the cluster");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        head_user_id: formData.head_user_id || null,
        clinic_ids: formData.clinic_ids
      };

      if (editMode && formData.id) {
        await apiClient.put(`/clusters/${formData.id}`, payload);
        toast.success("Cluster updated successfully!");
      } else {
        await apiClient.post("/clusters", payload);
        toast.success("Cluster created successfully!");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save cluster");
    }
  };

  const handleDelete = async (clusterId, clusterName) => {
    if (!confirm(`Are you sure you want to delete cluster "${clusterName}"?`)) return;
    
    try {
      await apiClient.delete(`/clusters/${clusterId}`);
      toast.success("Cluster deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete cluster");
    }
  };

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic?.name || "Unknown Clinic";
  };

  const getHeadName = (headId) => {
    const head = users.find(u => u.id === headId);
    return head?.name || head?.email || "Not Assigned";
  };

  // Filter clinics that are already in other clusters
  const getAvailableClinics = () => {
    const assignedClinicIds = clusters
      .filter(c => c.id !== formData.id) // Exclude current cluster in edit mode
      .flatMap(c => c.clinic_ids || []);
    
    return clinics.filter(c => 
      !assignedClinicIds.includes(c.id) || formData.clinic_ids.includes(c.id)
    );
  };

  const filteredClinics = getAvailableClinics().filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isSuperAdmin && user?.role !== "head") {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
          <p className="text-slate-500">Only Super Admins and Heads can manage clusters.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="cluster-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Layers className="w-7 h-7 text-indigo-600" />
              Cluster Management
            </h1>
            <p className="text-slate-500">Group clinics into regional clusters and assign heads</p>
          </div>

          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={handleOpenCreate}
            data-testid="create-cluster-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Cluster
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-indigo-600">Total Clusters</p>
                  <p className="text-xl font-bold text-indigo-700">{clusters.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Total Clinics</p>
                  <p className="text-xl font-bold text-emerald-700">{clinics.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-600">Assigned Clinics</p>
                  <p className="text-xl font-bold text-blue-700">
                    {clusters.reduce((sum, c) => sum + (c.clinic_ids?.length || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-amber-600">Unassigned Clinics</p>
                  <p className="text-xl font-bold text-amber-700">
                    {clinics.length - clusters.reduce((sum, c) => sum + (c.clinic_ids?.length || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clusters List */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-pulse">Loading clusters...</div>
            </CardContent>
          </Card>
        ) : clusters.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Clusters Yet</h3>
              <p className="text-slate-500 mb-4">Create your first cluster to group clinics by region</p>
              <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create First Cluster
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {clusters.map((cluster) => (
              <Card key={cluster.id} className="overflow-hidden" data-testid={`cluster-card-${cluster.id}`}>
                <div 
                  className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Layers className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{cluster.name}</h3>
                        <p className="text-sm text-slate-500">
                          <UserCog className="w-3 h-3 inline mr-1" />
                          Head: {getHeadName(cluster.head_user_id)}
                        </p>
                        {cluster.description && (
                          <p className="text-xs text-slate-400 mt-1">{cluster.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">
                        {cluster.clinic_count || cluster.clinic_ids?.length || 0} clinics
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleOpenEdit(cluster); }}
                        data-testid={`edit-cluster-${cluster.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); handleDelete(cluster.id, cluster.name); }}
                        data-testid={`delete-cluster-${cluster.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedCluster === cluster.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Clinic List */}
                {expandedCluster === cluster.id && (
                  <div className="border-t bg-slate-50/50 p-5">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Clinics in this Cluster
                    </h4>
                    {cluster.clinic_ids?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {cluster.clinic_ids.map((clinicId) => {
                          const clinic = clinics.find(c => c.id === clinicId);
                          return (
                            <div key={clinicId} className="p-3 bg-white rounded-lg border flex items-center gap-3">
                              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                  {clinic?.name || "Unknown"}
                                </p>
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {clinic?.city || "N/A"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No clinics assigned to this cluster</p>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? "Edit Cluster" : "Create New Cluster"}</DialogTitle>
              <DialogDescription>
                {editMode 
                  ? "Update cluster details and manage assigned clinics" 
                  : "Group clinics into a regional cluster and assign a head"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Cluster Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., North Region"
                    required
                    data-testid="cluster-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="head">Cluster Head</Label>
                  <Select
                    value={formData.head_user_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, head_user_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="cluster-head-select">
                      <SelectValue placeholder="Select a head" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Head Assigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this cluster"
                  data-testid="cluster-description-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Select Clinics *</Label>
                  <Badge variant="outline">
                    {formData.clinic_ids.length} selected
                  </Badge>
                </div>
                
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search clinics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {filteredClinics.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      No clinics available
                    </div>
                  ) : (
                    filteredClinics.map((clinic) => (
                      <div 
                        key={clinic.id} 
                        className={`flex items-center gap-3 p-3 border-b last:border-0 hover:bg-slate-50 cursor-pointer ${
                          formData.clinic_ids.includes(clinic.id) ? 'bg-indigo-50' : ''
                        }`}
                        onClick={() => handleClinicToggle(clinic.id)}
                      >
                        <Checkbox
                          checked={formData.clinic_ids.includes(clinic.id)}
                          onCheckedChange={() => handleClinicToggle(clinic.id)}
                          data-testid={`clinic-checkbox-${clinic.id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{clinic.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {clinic.city || "N/A"} • {clinic.address || "No address"}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-cluster-btn">
                  {editMode ? "Update Cluster" : "Create Cluster"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
